import type { ICustomFooterLinks, ICustomLink } from "@/components/Footer/footer.interface"
import type { IEventTemplateResponse, IEventTemplateResponseV1, IEventThemeJson } from "@/types"
import { ThemeJson } from "./themeJson"

export class EventTemplate {
  public id?: number
  public dateCreated?: Date
  public lastUpdated?: Date
  public name?: string
  public description?: string
  public isApplied?: boolean
  public headerImage?: File | null
  public footerImage?: File | null
  public pathToFooterImage?: string
  public pathToHeaderImage?: string
  public requireAdditionalHostTerms?: boolean
  public collectGuestEmailsForMarketing?: boolean
  public additionalHostTermsHtml?: string
  public optInSentenceHtml?: string
  public additionalHostTerm?: ICustomLink
  public optInSentence?: ICustomLink
  public customFooterLinks?: ICustomFooterLinks
  public themeJson?: IEventThemeJson

  /** @deprecated Legacy CSS-based event template model */
  public baseFontLabel?: string
  /** @deprecated Legacy CSS-based event template model */
  public baseFontValue?: string
  /** @deprecated Legacy CSS-based event template model */
  public headingFontLabel?: string
  /** @deprecated Legacy CSS-based event template model */
  public headingFontValue?: string
  /** @deprecated Legacy CSS-based event template model */
  public templateCss?: string

  constructor(template: Partial<EventTemplate>) {
    this.id = template.id
    this.dateCreated = template.dateCreated
    this.lastUpdated = template.lastUpdated
    this.name = template.name
    this.description = template.description
    this.isApplied = template.isApplied
    this.headerImage = template.headerImage
    this.footerImage = template.footerImage
    this.pathToHeaderImage = template.pathToHeaderImage
    this.pathToFooterImage = template.pathToFooterImage
    this.requireAdditionalHostTerms = template.requireAdditionalHostTerms
    this.collectGuestEmailsForMarketing = template.collectGuestEmailsForMarketing
    this.additionalHostTermsHtml = template.additionalHostTermsHtml
    this.optInSentenceHtml = template.optInSentenceHtml
    this.additionalHostTerm = template.additionalHostTerm
    this.optInSentence = template.optInSentence
    this.customFooterLinks = template.customFooterLinks
    this.themeJson = template.themeJson

    this.baseFontLabel = template.baseFontLabel
    this.baseFontValue = template.baseFontValue
    this.headingFontLabel = template.headingFontLabel
    this.headingFontValue = template.headingFontValue
    this.templateCss = template.templateCss
  }

  public static fromResponse(response?: IEventTemplateResponse) {
    const dateCreated = response?.DateCreated ? new Date(response.DateCreated) : undefined
    const lastUpdated = response?.LastUpdated ? new Date(response.LastUpdated) : undefined

    const customSocialLinks = response?.TemplateSettings?.CustomFooterLinks?.SocialLinks
    const customFooterLinks = response?.TemplateSettings?.CustomFooterLinks?.FooterLinks ?? []

    const additionalHostTerm = response?.AdditionalHostTerm && {
      key: response.AdditionalHostTerm.Key,
      value: response.AdditionalHostTerm.Value,
    }

    const optInSentence = response?.OptInSentence && {
      key: response.OptInSentence.Key,
      value: response.OptInSentence.Value,
    }

    const themeJson = response?.ThemeJSON && ThemeJson.fromResponse(response.ThemeJSON)

    return new EventTemplate({
      id: response?.EventTemplateID ?? response?.Id,
      dateCreated,
      lastUpdated,
      name: response?.Name ?? "—",
      description: response?.Description,
      templateCss: response?.TemplateCSS,
      baseFontLabel: response?.BaseFontLabel,
      baseFontValue: response?.BaseFontValue,
      headingFontLabel: response?.HeadingFontLabel,
      headingFontValue: response?.HeadingFontValue,
      pathToHeaderImage: response?.PathToHeaderImage,
      pathToFooterImage: response?.PathToFooterImage,
      themeJson,
      requireAdditionalHostTerms: response?.RequireAdditionalHostTerms ?? false,
      collectGuestEmailsForMarketing: response?.CollectGuestEmailsForMarketing ?? false,
      additionalHostTermsHtml: response?.AdditionalHostTermsHTML,
      optInSentenceHtml: response?.OptInSentenceHTML,
      additionalHostTerm,
      optInSentence,
      customFooterLinks: {
        socialLinks: {
          facebook: customSocialLinks?.Facebook ?? "",
          instagram: customSocialLinks?.Instagram ?? "",
          youtube: customSocialLinks?.Youtube ?? "",
          tiktok: customSocialLinks?.Tiktok ?? "",
          pinterest: customSocialLinks?.Pinterest ?? "",
          linkedIn: customSocialLinks?.LinkedIn ?? "",
          x: customSocialLinks?.X ?? "",
        },
        footerLinks: customFooterLinks?.map(link => ({
          key: link.Key,
          value: link.Value ?? "",
        })),
      },

      isApplied: response?.IsApplied,
    })
  }

  public static fromResponseV1(response?: IEventTemplateResponseV1) {
    const dateCreated = response?.dateCreated ? new Date(response.dateCreated) : undefined
    const lastUpdated = response?.lastUpdated ? new Date(response.lastUpdated) : undefined
    const themeJson = response?.themeJSON && ThemeJson.fromResponseV1(response.themeJSON)

    return new EventTemplate({
      id: response?.eventTemplateID ?? response?.id,
      dateCreated,
      lastUpdated,
      name: response?.name ?? "—",
      description: response?.description,
      templateCss: response?.templateCSS,
      baseFontLabel: response?.baseFontLabel,
      baseFontValue: response?.baseFontValue,
      headingFontLabel: response?.headingFontLabel,
      headingFontValue: response?.headingFontValue,
      pathToHeaderImage: response?.pathToHeaderImage,
      pathToFooterImage: response?.pathToFooterImage,
      themeJson,
      requireAdditionalHostTerms: response?.requireAdditionalHostTerms ?? false,
      collectGuestEmailsForMarketing: response?.collectGuestEmailsForMarketing ?? false,
      additionalHostTermsHtml: response?.additionalHostTermsHtml,
      optInSentenceHtml: response?.optInSentenceHtml,
      additionalHostTerm: response?.additionalHostTerm,
      optInSentence: response?.optInSentence,
      customFooterLinks: response?.templateSettings?.customFooterLinks,
      isApplied: response?.isApplied,
    })
  }
}
